import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import Button from '../common/Button';
import DropdownMenu from '../common/DropdownMenu';
import CancelDeleteModal from './CancelDeleteModal';
import EditSessionModal from './EditSessionModal';
import SessionNavigationButton from '../common/SessionNavigationButton';
import { isMissedPlannedSession } from './hub/sessionHubUtils';
import BlockBookingBadge from './BlockBookingBadge';
import HostSelectBadge from './HostSelectBadge';
import SessionSeriesBadge from './SessionSeriesBadge';
import { isHostSelectSignup } from '../../utils/signupSelection';

const getStatusMeta = (session) => {
  const s = session.session_status;
  if (s === 'upcoming') return { label: 'Open', className: 'bg-success-light text-success-dark' };
  if (s === 'draft') return { label: 'Draft', className: 'bg-warning-light text-warning-dark' };
  if (s === 'ongoing') return { label: 'Live', className: 'bg-primary/10 text-primary' };
  if (s === 'cancelled') return { label: 'Cancelled', className: 'bg-danger-light text-danger-dark' };
  if (s === 'completed') return { label: 'Done', className: 'bg-surface-subtle text-text-muted' };
  return { label: s || '—', className: 'bg-surface-subtle text-text-muted' };
};

const formatTime = (timeString) => {
  if (!timeString) return '—';
  if (timeString.includes('T') || timeString.includes(' ')) {
    return format(new Date(timeString), 'h:mm a');
  }
  const [h, m] = timeString.split(':');
  const d = new Date();
  d.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);
  return format(d, 'h:mm a');
};

const formatDateParts = (dateString) => {
  if (!dateString) return { weekday: '—', day: '—', month: '—', isToday: false };
  const d = new Date(dateString);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  return {
    weekday: isToday ? 'Today' : format(d, 'EEE'),
    day: format(d, 'd'),
    month: format(d, 'MMM'),
    isToday,
  };
};

/**
 * Responsive list of planned sessions for club admins.
 * Each session is a row card — no scroll trap, dropdowns render in a portal.
 */
const PlannedSessionsTable = ({
  sessions,
  clubId,
  onEditSession,
  onUpdateSessionStatus,
  onCancelSession,
  onUncancelSession,
  onDeleteSession,
  onManageSignups,
  currentUser,
}) => {
  const [showCancelDeleteModalFor, setShowCancelDeleteModalFor] = useState(null);
  const [showEditModalFor, setShowEditModalFor] = useState(null);

  const sortedSessions = useMemo(() => {
    return [...sessions].sort((a, b) => {
      const dateA = a.session_date || a.date;
      const dateB = b.session_date || b.date;
      if (dateA !== dateB) return new Date(dateA) - new Date(dateB);
      const timeA = a.start_time || a.time || '00:00';
      const timeB = b.start_time || b.time || '00:00';
      return timeA.localeCompare(timeB);
    });
  }, [sessions]);

  const getDropdownItems = (session) => {
    const items = [
      { label: 'Edit Session', icon: '✏️', onClick: () => setShowEditModalFor(session.id) },
    ];
    if (session.session_status === 'upcoming') {
      items.push({
        label: 'Make Draft',
        icon: '📝',
        onClick: () => onUpdateSessionStatus(session.id, 'draft'),
      });
    } else if (session.session_status === 'draft') {
      items.push({
        label: 'Make Live',
        icon: '✅',
        onClick: () => onUpdateSessionStatus(session.id, 'upcoming'),
      });
    }
    items.push(
      { label: 'View Signups', icon: '👥', onClick: () => onManageSignups(session.id) },
      { divider: true },
      {
        label: 'Cancel Session',
        icon: '❌',
        onClick: () => setShowCancelDeleteModalFor(session.id),
        variant: 'danger',
      }
    );
    return items;
  };

  const handleSessionUpdated = async (sessionId, formData) => {
    if (onEditSession) await onEditSession(sessionId, formData);
    setShowEditModalFor(null);
  };

  const confirmedCount = (s) => s.confirmed_count ?? s.current_players ?? 0;
  const waitlistCount = (s) => s.waitlist_count ?? s.waitlist_players ?? 0;
  const maxPlayers = (s) => s.max_players ?? 16;

  if (sortedSessions.length === 0) return null;

  return (
    <>
      <ul className="space-y-3">
        {sortedSessions.map((session) => {
          const status = getStatusMeta(session);
          const isCancelled = session.session_status === 'cancelled';
          const isOngoing = session.session_status === 'ongoing';
          const isMissed = isMissedPlannedSession(session);
          const dateParts = formatDateParts(session.session_date || session.date);
          const timeLabel = formatTime(session.start_time || session.time);

          return (
            <li
              key={session.id}
              className={`
                rounded-xl border border-border-medium bg-surface overflow-visible
                ${
                  isCancelled
                    ? 'border-danger/30 bg-danger-light/20'
                    : isMissed
                      ? 'border-warning/40 bg-warning-light/20'
                      : 'hover:border-border-strong hover:shadow-sm'
                }
                transition-shadow
              `}
            >
              <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:gap-5">
                {/* Date pill */}
                <div
                  className={`
                    flex shrink-0 items-center gap-3 sm:flex-col sm:gap-0 sm:w-16 sm:text-center
                    rounded-lg bg-surface-subtle border border-border-light px-3 py-2 sm:px-2 sm:py-3
                    ${dateParts.isToday ? 'border-primary/30 bg-primary/5' : ''}
                  `}
                >
                  <span className="text-xs font-semibold uppercase tracking-wide text-text-secondary sm:mb-0.5">
                    {dateParts.weekday}
                  </span>
                  <span className="text-xl font-bold text-text-primary leading-none sm:my-0.5">
                    {dateParts.day}
                  </span>
                  <span className="text-xs text-text-muted sm:hidden">
                    {dateParts.month}
                  </span>
                  <span className="hidden text-xs text-text-muted sm:block">
                    {dateParts.month}
                  </span>
                </div>

                {/* Session info */}
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-semibold text-text-primary truncate">
                      {session.name || 'Untitled session'}
                    </h3>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${status.className}`}>
                      {status.label}
                    </span>
                    <HostSelectBadge session={session} />
                    {isMissed && !isCancelled ? (
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-warning-light text-warning-dark border border-warning/30">
                        Past date
                      </span>
                    ) : null}
                  </div>

                  {isMissed && !isCancelled ? (
                    <p className="text-xs text-warning-dark">
                      This date has passed — cancel or delete if the session will not run.
                    </p>
                  ) : null}

                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-text-secondary">
                    <span>{timeLabel}</span>
                    <span className="text-border-medium" aria-hidden>·</span>
                    {isHostSelectSignup(session) ? (
                      <>
                        <span>
                          {confirmedCount(session)} / {maxPlayers(session)} confirmed
                        </span>
                        <span className="text-border-medium" aria-hidden>·</span>
                        <span>{waitlistCount(session)} on the list</span>
                      </>
                    ) : (
                      <span>
                        {confirmedCount(session)} / {maxPlayers(session)} signed up
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {session.session_types && (
                      <span className="inline-flex px-2 py-0.5 rounded-md text-xs bg-surface-subtle text-text-secondary border border-border-light">
                        {session.session_types.name}
                      </span>
                    )}
                    <SessionSeriesBadge session={session} />
                    <BlockBookingBadge session={session} />
                    {!session.is_public && !session.session_types && (
                      <span className="inline-flex px-2 py-0.5 rounded-md text-xs bg-surface-subtle text-text-secondary border border-border-light">
                        Members only
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions — full width on mobile */}
                <div className="flex flex-wrap items-stretch sm:items-center gap-2 sm:shrink-0 sm:justify-end">
                  {!isCancelled && !isMissed && (
                    <SessionNavigationButton
                      session={session}
                      clubId={clubId}
                      starterPlayerId={currentUser?.id}
                      action={isOngoing ? 'enter' : 'start'}
                      variant="primary"
                      size="small"
                      disabled={session.session_status !== 'upcoming' && !isOngoing}
                      className="flex-1 sm:flex-none"
                    />
                  )}
                  {isMissed && !isCancelled ? (
                    <Button
                      variant="outline"
                      size="small"
                      className="flex-1 border-warning text-warning-dark hover:bg-warning-light sm:flex-none"
                      onClick={() => setShowCancelDeleteModalFor(session.id)}
                    >
                      Cancel or delete
                    </Button>
                  ) : null}
                  {isCancelled && (
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => onUncancelSession(session.id)}
                      className="flex-1 sm:flex-none"
                    >
                      Restore
                    </Button>
                  )}
                  <DropdownMenu
                    usePortal
                    trigger={
                      <Button variant="outline" size="small" className="flex-1 sm:flex-none">
                        More
                      </Button>
                    }
                    items={getDropdownItems(session)}
                    align="right"
                  />
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {showCancelDeleteModalFor && (() => {
        const session = sessions.find((s) => s.id === showCancelDeleteModalFor);
        if (!session) return null;
        return (
          <CancelDeleteModal
            isOpen={true}
            onClose={() => setShowCancelDeleteModalFor(null)}
            onCancel={() => {
              onCancelSession(session.id);
              setShowCancelDeleteModalFor(null);
            }}
            onDelete={() => {
              onDeleteSession(session.id);
              setShowCancelDeleteModalFor(null);
            }}
            sessionName={session.name || 'Session'}
          />
        );
      })()}

      {showEditModalFor && (() => {
        const session = sessions.find((s) => s.id === showEditModalFor);
        if (!session) return null;
        return (
          <EditSessionModal
            isOpen={true}
            onClose={() => setShowEditModalFor(null)}
            session={session}
            selectedClub={session.clubs}
            clubId={clubId}
            currentUser={currentUser}
            onSessionUpdated={handleSessionUpdated}
          />
        );
      })()}
    </>
  );
};

export default PlannedSessionsTable;
